import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Sparkles, Leaf, Wind, Flower, Activity, Droplet, Heart, Moon, Bed, Sun } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { api } from '@/lib/api';
import { HOME } from '@/constants/testIds';
import SEO, { orgSchema } from '@/components/SEO';

const ICON = { sparkles: Sparkles, leaf: Leaf, wind: Wind, flower: Flower, activity: Activity, droplet: Droplet, heart: Heart, moon: Moon, bed: Bed, sun: Sun };
const HERO_IMG = 'https://images.unsplash.com/photo-1503891617560-5b8c2e28cbf6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2FsbSUyMG5hdHVyZSUyMGFic3RyYWN0fGVufDB8fHx8MTc4NDkxNjQ3MXww&ixlib=rb-4.1.0&q=85';
const CONSULT_IMG = 'https://images.pexels.com/photos/7088834/pexels-photo-7088834.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200';
const EDU_IMG = 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxjYWxtJTIwcGVhY2VmdWwlMjBtZWRpdGF0aW9uJTIwd2VsbG5lc3N8ZW58MHx8fHwxNzg0OTE2NDcxfDA&ixlib=rb-4.1.0&q=85';

const useReveal = (deps = []) => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal:not(.in)');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

const WisdomBlock = () => {
  const [verse, setVerse] = useState(null);
  useEffect(() => { api.get('/content/verses/random').then(({ data }) => setVerse(data)).catch(() => {}); }, []);
  if (!verse) return null;
  return (
    <div className="grid md:grid-cols-12 gap-12 reveal">
      <div className="md:col-span-4">
        <div className="sans-label mb-4" style={{ color: '#8A9A5B' }}>Wisdom from the Ayurvedic Classics</div>
        <h2 className="sans-h2">A verse, every visit.</h2>
        <p className="sans-body mt-6 max-w-sm">Rotating verses from Charaka, Sushruta, and Ashtanga Hridaya — the seed texts of Ayurveda.</p>
      </div>
      <div className="md:col-span-8 md:pl-8 border-l md:border-l border-sans_line md:pl-12">
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: '#8A9A5B' }}>{verse.source_text} · {verse.chapter}</div>
        {verse.sanskrit && (
          <div className="font-serif text-3xl md:text-4xl mt-5 leading-[1.35] text-sans_ink">{verse.sanskrit}</div>
        )}
        {verse.transliteration && <div className="mt-3 italic text-sans_mute text-sm leading-relaxed">{verse.transliteration}</div>}
        {verse.translation && <div className="mt-6 font-serif text-xl text-sans_ink leading-relaxed italic">"{verse.translation}"</div>}
        {verse.explanation && <p className="mt-6 text-sans_mute leading-relaxed">{verse.explanation}</p>}
        {verse.practical_relevance && (
          <div className="mt-6 pt-6 border-t border-sans_line">
            <div className="sans-label mb-2">Why it matters</div>
            <p className="text-sans_ink leading-relaxed">{verse.practical_relevance}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Home = () => {
  const [concerns, setConcerns] = useState([]);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  useReveal([concerns.length, products.length, testimonials.length, faqs.length]);

  useEffect(() => {
    api.get('/content/health-concerns').then(({ data }) => setConcerns(data)).catch(() => {});
    api.get('/shop/products/featured').then(({ data }) => setProducts(data)).catch(() => {});
    api.get('/content/testimonials').then(({ data }) => setTestimonials(data)).catch(() => {});
    api.get('/content/faqs').then(({ data }) => setFaqs(data)).catch(() => {});
  }, []);

  return (
    <div className="bg-sans_bone">
      <SEO
        title="Authentic classical Ayurveda, made personal"
        description="Free Prakriti Assessment, online consultations with qualified Ayurvedic doctors, and classical formulations. Rooted in tradition, delivered thoughtfully."
        schema={orgSchema}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="sans-container pt-16 md:pt-24 pb-20 md:pb-32 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-7 relative z-10">
            <div className="sans-label mb-6 animate-fade-in">Free • Prakriti Assessment</div>
            <h1 className="sans-h1 animate-fade-up">Understand your<br/>Ayurvedic constitution.</h1>
            <p className="sans-body mt-8 max-w-xl text-lg animate-fade-up" style={{ animationDelay: '120ms' }}>
              A free, adaptive assessment rooted in classical Ayurvedic principles.
              About five minutes. A clear starting point for a considered health journey.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: '240ms' }}>
              <Link to="/assessment" data-testid={HOME.heroStartAssessment} className="sans-btn-primary group">
                Start free assessment
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
              </Link>
              <Link to="/consultation" data-testid={HOME.heroBookConsultation} className="sans-btn-secondary">
                Book an online consultation
              </Link>
            </div>
            <div className="mt-14 flex items-center gap-8 text-xs uppercase tracking-[0.2em] text-sans_mute animate-fade-in" style={{ animationDelay: '360ms' }}>
              <div>Qualified BAMS doctors</div>
              <div className="w-1 h-1 rounded-full bg-sans_mute" />
              <div>Classical formulations</div>
              <div className="hidden sm:block w-1 h-1 rounded-full bg-sans_mute" />
              <div className="hidden sm:block">Private &amp; secure</div>
            </div>
          </div>
          <div className="md:col-span-5 relative">
            <div className="aspect-[4/5] relative overflow-hidden bg-sans_line">
              <img src={HERO_IMG} alt="Calm nature" className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 bg-gradient-to-t from-black/40 via-black/10 to-transparent text-white">
                <div className="text-[10px] uppercase tracking-[0.22em] opacity-80">Rooted in classical Ayurveda</div>
                <div className="font-serif text-2xl mt-2">Personalized, patient, precise.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HEALTH CONCERNS */}
      <section className="border-t border-sans_line">
        <div className="sans-container sans-section">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-6 reveal">
              <div className="sans-label mb-4">Where to begin</div>
              <h2 className="sans-h2">What brings you here today?</h2>
            </div>
            <p className="md:col-span-6 sans-body md:pl-8 reveal">
              Every Ayurvedic journey begins with what you actually feel. Choose what resonates —
              this is not a diagnosis, only a starting point for conversation.
            </p>
          </div>
          <div className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-px bg-sans_line border border-sans_line">
            {concerns.map((c) => {
              const Icon = ICON[c.icon] || Leaf;
              const accent = c.color || '#8A9A86';
              return (
                <Link
                  key={c.slug}
                  to={`/products?concern=${c.slug}`}
                  data-testid={`concern-${c.slug}`}
                  className="group relative bg-sans_bone p-8 md:p-10 hover:bg-white transition-colors duration-500 overflow-hidden"
                  style={{ '--accent': accent }}
                >
                  <span aria-hidden className="absolute top-0 left-0 h-full w-[3px] transition-all duration-300 group-hover:w-1.5" style={{ background: accent }} />
                  <Icon className="w-5 h-5 mb-6 transition-colors" strokeWidth={1.25} style={{ color: accent }} />
                  <div className="font-serif text-xl md:text-2xl text-sans_ink">{c.name}</div>
                  <div className="mt-2 text-xs text-sans_mute leading-relaxed">{c.description}</div>
                  <ArrowUpRight className="mt-6 w-4 h-4 text-sans_mute group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" strokeWidth={1.5} style={{ color: accent }} />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* PERSONALISED DIET — hero-style feature before products */}
      <section className="border-t border-sans_line bg-white">
        <div className="sans-container sans-section">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5 reveal">
              <div className="sans-label mb-6" style={{ color: '#8A9A5B' }}>Eat for your body, not a template</div>
              <h2 className="sans-h2 leading-tight">Personalised<br/>Ayurvedic-Inspired Diet</h2>
              <p className="sans-body mt-8 max-w-md">
                A modern, enjoyable approach to eating according to your individual body type and lifestyle.
                Begin with a personal consultation with our Diet Expert. Your individual plan is then personally prepared and delivered to your Saans Dashboard.
              </p>
              <p className="mt-4 text-sm italic text-sans_mute max-w-md">Personally prepared by a specialist — not an AI-generated diet chart.</p>
              <div className="mt-10 flex items-baseline gap-6">
                <div className="font-serif text-4xl text-sans_ink">₹2,900</div>
                <div className="text-xs uppercase tracking-[0.2em] text-sans_mute">Consultation + Plan</div>
              </div>
              <Link to="/consultation/diet" data-testid="home-diet-cta" className="sans-btn-primary group mt-10 inline-flex">
                Get my personalised plan
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
              </Link>
            </div>

            <div className="md:col-span-7 md:pl-8 reveal">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { t: 'Morning Drink', d: 'Saffron Almond · Fennel-Pear Cooler', bg: '#EFE4D2' },
                  { t: 'Breakfast', d: 'Millet Pancakes · Avocado Toast', bg: '#E4EAD8' },
                  { t: 'Lunch', d: 'Tandoori Paneer Bowl · Mediterranean Khichdi', bg: '#EAD8CE' },
                  { t: 'Snacks', d: 'Sweet Potato Chaat · Hummus Pita', bg: '#E9E1CE' },
                  { t: 'Dinner', d: 'Herbed Paneer Kebabs · Pumpkin Pasta', bg: '#DCE0D0' },
                  { t: 'Night', d: 'Nutmeg Almond · Saffron Milk', bg: '#E7DAC9' },
                ].map((m) => (
                  <div key={m.t} className="relative aspect-[4/3] p-6 border border-sans_line overflow-hidden flex flex-col justify-end" style={{ background: m.bg }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-sans_ink/70">{m.t}</div>
                    <div className="mt-2 font-serif text-lg text-sans_ink leading-snug">{m.d}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-center gap-3 text-xs text-sans_mute">
                <span className="inline-block w-8 h-px bg-sans_mute" />
                <span className="italic">Sample plan — your actual dishes will be personalised for you</span>
              </div>

              {/* Ayurveda food principles */}
              <div className="mt-14 grid grid-cols-3 gap-6 md:gap-8">
                <div>
                  <div className="w-10 h-10 rounded-full bg-sans_bone border border-sans_line flex items-center justify-center mb-4">
                    <span className="font-serif text-lg text-sans_ink">01</span>
                  </div>
                  <div className="font-serif text-lg text-sans_ink">Food that suits YOU</div>
                  <div className="mt-2 text-xs text-sans_mute leading-relaxed">The same salad heals one person and bloats another. Your plate is designed for your Prakriti, agni, and season.</div>
                </div>
                <div>
                  <div className="w-10 h-10 rounded-full bg-sans_bone border border-sans_line flex items-center justify-center mb-4">
                    <span className="font-serif text-lg text-sans_ink">02</span>
                  </div>
                  <div className="font-serif text-lg text-sans_ink">Enjoyable, not boring</div>
                  <div className="mt-2 text-xs text-sans_mute leading-relaxed">Ayurvedic eating doesn't mean bland. Indian, Mediterranean, Asian, modern — dishes you'd want to eat, made mindful.</div>
                </div>
                <div>
                  <div className="w-10 h-10 rounded-full bg-sans_bone border border-sans_line flex items-center justify-center mb-4">
                    <span className="font-serif text-lg text-sans_ink">03</span>
                  </div>
                  <div className="font-serif text-lg text-sans_ink">Six tastes, right balance</div>
                  <div className="mt-2 text-xs text-sans_mute leading-relaxed">Sweet · Sour · Salty · Pungent · Bitter · Astringent. Your specialist tunes each meal so nothing is missing and nothing overpowers.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-t border-sans_line bg-white">
        <div className="sans-container sans-section">
          <div className="flex items-end justify-between mb-14 reveal">
            <div>
              <div className="sans-label mb-4">Classical formulations</div>
              <h2 className="sans-h2">Featured products</h2>
            </div>
            <Link to="/products" className="hidden md:inline-flex sans-btn-ghost">View all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {products.slice(0, 4).map((p) => (
              <Link key={p.product_id} to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="group reveal">
                <div className="aspect-square bg-sans_bone border border-sans_line overflow-hidden flex items-center justify-center p-4 md:p-5">
                  <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain group-hover:scale-[1.05] transition-transform duration-700" />
                </div>
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-sans_mute">{p.size}</div>
                  <div className="font-serif text-lg md:text-xl text-sans_ink mt-1">{p.name}</div>
                  <div className="text-sm text-sans_mute mt-1">{p.tagline}</div>
                  <div className="text-sm text-sans_ink mt-3">₹{p.price}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW CONSULTATION WORKS */}
      <section className="border-t border-sans_line">
        <div className="sans-container sans-section grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5 reveal">
            <div className="aspect-[4/5] bg-sans_line overflow-hidden">
              <img src={CONSULT_IMG} alt="Consultation" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7 md:pl-8 reveal">
            <div className="sans-label mb-4">How consultation works</div>
            <h2 className="sans-h2">A conversation, not a checklist.</h2>
            <p className="sans-body mt-6 max-w-lg">
              A qualified Ayurvedic doctor considers your full picture — medical history, symptoms, lifestyle,
              and constitution — before making any recommendation.
            </p>
            <div className="mt-12 space-y-8">
              {[
                { n: '01', t: 'Choose a time that suits you', d: 'Pick from available slots. You will get a confirmation and calendar invite.' },
                { n: '02', t: 'Meet online, one-on-one', d: 'A private video consultation with a BAMS-qualified doctor. 30–40 minutes.' },
                { n: '03', t: 'Digital prescription', d: 'Delivered to your email. Use with Saans Ayurved or any pharmacy.' },
              ].map((s) => (
                <div key={s.n} className="grid grid-cols-12 gap-4 items-start">
                  <div className="col-span-2 font-serif text-2xl text-sans_ink">{s.n}</div>
                  <div className="col-span-10">
                    <div className="font-serif text-xl text-sans_ink">{s.t}</div>
                    <div className="mt-2 text-sm text-sans_mute leading-relaxed max-w-md">{s.d}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/consultation" data-testid={HOME.finalCtaBook} className="sans-btn-primary mt-14">
              Book consultation — ₹1000
            </Link>
          </div>
        </div>
      </section>

      {/* PERSONALIZED VS GENERIC */}
      <section className="border-t border-sans_line bg-sans_ink text-sans_bone">
        <div className="sans-container sans-section grid md:grid-cols-2 gap-16">
          <div className="reveal">
            <div className="sans-label text-white/50 mb-4">Why personalized</div>
            <h2 className="sans-h2 text-sans_bone font-serif">Two people with the same symptom<br/>can need very different care.</h2>
          </div>
          <div className="reveal space-y-6 text-sans_bone/80 text-lg leading-relaxed md:pl-8">
            <p>Classical Ayurveda has always been individual. Your constitution, current imbalances, lifestyle, and environment matter — a generic recommendation cannot see any of that.</p>
            <p>The Prakriti Assessment gives you a starting point. A qualified doctor takes it from there.</p>
          </div>
        </div>
      </section>

      {/* EDUCATIONAL RESOURCES */}
      <section className="border-t border-sans_line bg-white">
        <div className="sans-container sans-section">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-5 reveal">
              <div className="aspect-[4/5] bg-sans_line overflow-hidden">
                <img src={EDU_IMG} alt="Learn" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="md:col-span-7 md:pl-8 reveal">
              <div className="sans-label mb-4">Learn</div>
              <h2 className="sans-h2">Ayurveda, quietly explained.</h2>
              <p className="sans-body mt-6 max-w-lg">
                Foundations, doshas, daily rhythms, seasonal wellness — written without mysticism or marketing.
              </p>
              <Link to="/learn" className="inline-flex items-center mt-8 text-sans_ink border-b border-sans_ink pb-1 tracking-wide text-sm hover:opacity-70">
                Explore the library →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-t border-sans_line">
        <div className="sans-container sans-section">
          <div className="max-w-2xl reveal">
            <div className="sans-label mb-4">Voices from our community</div>
            <h2 className="sans-h2">Care that felt personal.</h2>
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-px bg-sans_line border border-sans_line">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-sans_bone p-10 reveal">
                <div className="font-serif text-xl md:text-2xl text-sans_ink leading-snug">"{t.text}"</div>
                <div className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-sans_mute">
                  <span>{t.name}</span><span className="w-1 h-1 rounded-full bg-sans_mute" /><span>{t.location}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-sans_line bg-white">
        <div className="sans-container sans-section grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4 reveal">
            <div className="sans-label mb-4">Questions</div>
            <h2 className="sans-h2">Common questions.</h2>
            <p className="sans-body mt-6">Everything about the assessment, consultations, and our approach.</p>
          </div>
          <div className="md:col-span-8 reveal">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-sans_line">
                  <AccordionTrigger data-testid={`faq-${i}`} className="text-left font-serif text-lg md:text-xl text-sans_ink hover:no-underline py-6">
                    {f.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sans_mute text-base leading-relaxed pb-6">
                    {f.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* AYURVEDIC WISDOM */}
      <section className="border-t border-sans_line bg-sans_bone">
        <div className="sans-container sans-section">
          <WisdomBlock />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-sans_line">
        <div className="sans-container sans-section text-center">
          <div className="sans-label mb-4">Ready to begin?</div>
          <h2 className="sans-h1 max-w-4xl mx-auto reveal">Take five minutes for a<br/>lifetime of feeling better.</h2>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link to="/assessment" className="sans-btn-primary">Start free assessment</Link>
            <Link to="/consultation" className="sans-btn-secondary">Book consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
